import { useEffect, useMemo } from 'react'
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form'
import { AlertTriangle, Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { getErrorMessage, getErrorStatus } from '@/api/errors'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { useCreateVariableGroup, useUpdateVariableGroup, useVariableGroup } from '@/hooks'
import { extractValidationErrors } from '@/lib/validationErrors'
import type { SaveVariableGroupRequest, VariableGroup } from '@/types'

const KEY_PATTERN = /^[A-Z_][A-Z0-9_]*$/
const KEEP_SECRET_HINT = 'Leave empty to keep the current value'

interface VariableRow {
  id?: string
  key: string
  value: string
  isSecret: boolean
  savedSecret: boolean
}

interface FormValues {
  name: string
  description: string
  variables: VariableRow[]
}

const emptyRow = (): VariableRow => ({ key: '', value: '', isSecret: false, savedSecret: false })

function toFormValues(group?: VariableGroup): FormValues {
  if (!group) return { name: '', description: '', variables: [emptyRow()] }

  return {
    name: group.name,
    description: group.description ?? '',
    variables: group.variables.map(v => ({
      id: v.id,
      key: v.key,
      value: v.isSecret ? '' : (v.value ?? ''),
      isSecret: v.isSecret,
      savedSecret: v.isSecret,
    })),
  }
}

function toRequest(values: FormValues): SaveVariableGroupRequest {
  return {
    name: values.name.trim(),
    description: values.description.trim() || undefined,
    variables: values.variables.map(v => ({
      id: v.id,
      key: v.key.trim(),
      value: v.isSecret && v.savedSecret && v.value.trim() === '' ? undefined : v.value,
      isSecret: v.isSecret,
    })),
  }
}

interface VariableGroupDialogProps {
  projectId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  groupId?: string
}

export function VariableGroupDialog({ projectId, open, onOpenChange, groupId }: VariableGroupDialogProps) {
  const isEdit = !!groupId
  const { data: group, isError: loadFailed, error: loadError, isFetchedAfterMount } = useVariableGroup(projectId, open ? groupId : undefined)
  const createGroup = useCreateVariableGroup(projectId)
  const updateGroup = useUpdateVariableGroup(projectId)

  const { control, register, handleSubmit, reset, setError, clearErrors, formState: { errors, isSubmitting } } = useForm<FormValues>({
    defaultValues: toFormValues(),
  })
  const { fields, append, remove } = useFieldArray({ control, name: 'variables' })
  const watched = useWatch({ control })

  useEffect(() => {
    if (!open) return
    reset(toFormValues(isEdit ? group : undefined))
  }, [open, isEdit, group, reset])

  const showRenameWarning = useMemo(() => {
    if (!isEdit || !group) return false
    if ((watched.name ?? '').trim() !== group.name) return true
    const storedKeys = new Map(group.variables.map(v => [v.id, v.key]))
    return (watched.variables ?? []).some(v => v?.id && storedKeys.get(v.id) !== (v.key ?? '').trim())
  }, [isEdit, group, watched])

  const validateRows = (values: FormValues) => {
    let valid = true
    const seen = new Set<string>()

    values.variables.forEach((row, index) => {
      const key = row.key.trim()
      if (!key) {
        setError(`variables.${index}.key`, { message: 'Key is required.' })
        valid = false
      } else if (!KEY_PATTERN.test(key)) {
        setError(`variables.${index}.key`, { message: "Use uppercase letters, digits and '_' only; don't start with a digit." })
        valid = false
      } else if (seen.has(key)) {
        setError(`variables.${index}.key`, { message: `Key '${key}' is used more than once in this group.` })
        valid = false
      }
      seen.add(key)

      const keepsSavedSecret = row.isSecret && row.savedSecret
      if (!row.value.trim() && !keepsSavedSecret) {
        setError(`variables.${index}.value`, { message: 'Value is required.' })
        valid = false
      }
    })

    return valid
  }

  const applyServerErrors = (error: unknown) => {
    const serverErrors = extractValidationErrors(error)
    if (!serverErrors) {
      setError('root.server', { message: getErrorMessage(error) ?? 'Failed to save the environment variable group.' })
      return
    }

    const unmatched: string[] = []
    for (const { field, errorMessage } of serverErrors) {
      const rowMatch = /^Variables\[(\d+)\]\.(Key|Value)$/.exec(field)
      if (rowMatch) {
        const property = rowMatch[2] === 'Key' ? 'key' : 'value'
        setError(`variables.${Number(rowMatch[1])}.${property}`, { message: errorMessage })
      } else if (field === 'Name') {
        setError('name', { message: errorMessage })
      } else {
        unmatched.push(errorMessage)
      }
    }
    if (unmatched.length > 0) setError('root.server', { message: unmatched.join(' ') })
  }

  const onSubmit = async (values: FormValues) => {
    clearErrors('root')
    if (!validateRows(values)) return

    try {
      if (isEdit) {
        await updateGroup.mutateAsync({ groupId: groupId!, group: { ...toRequest(values), lastUpdatedAt: group!.lastUpdatedAt } })
        toast.success(`Environment variable group "${values.name.trim()}" saved.`)
      } else {
        await createGroup.mutateAsync(toRequest(values))
        toast.success(`Environment variable group "${values.name.trim()}" created.`)
      }
      onOpenChange(false)
    } catch (error) {
      applyServerErrors(error)
    }
  }

  const loadingGroup = isEdit && !loadFailed && (!isFetchedAfterMount || !group)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Environment Variable Group' : 'New Environment Variable Group'}</DialogTitle>
          <DialogDescription className="sr-only">
            Define the group name, description and its variables. Secret values are write-only.
          </DialogDescription>
        </DialogHeader>

        {isEdit && loadFailed ? (
          <p role="alert" className="text-sm text-destructive">
            {getErrorStatus(loadError) === 404
              ? 'This environment variable group no longer exists. It may have been deleted by someone else.'
              : (getErrorMessage(loadError) ?? 'Failed to load the environment variable group.')}
          </p>
        ) : loadingGroup ? (
          <div className="space-y-3" role="status" aria-label="Loading environment variable group">
            {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
          </div>
        ) : (
          <form id="variable-group-form" onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="variable-group-name">Group Name <span className="text-destructive">*</span></Label>
              <Input
                id="variable-group-name"
                aria-invalid={!!errors.name}
                {...register('name', {
                  validate: value => value.trim().length > 0 || 'Group name is required.',
                  maxLength: { value: 100, message: 'Group name must be 100 characters or fewer.' },
                })}
              />
              {errors.name && <p role="alert" className="text-xs text-destructive">{errors.name.message}</p>}
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="variable-group-description">Description</Label>
              <Input
                id="variable-group-description"
                {...register('description', { maxLength: { value: 500, message: 'Description must be 500 characters or fewer.' } })}
              />
              {errors.description && <p role="alert" className="text-xs text-destructive">{errors.description.message}</p>}
            </div>

            <fieldset className="flex flex-col gap-2">
              <legend className="mb-1 text-sm font-medium">Environment Variables</legend>
              <div className="rounded-lg border">
                <div className="grid grid-cols-[1fr_1.4fr_72px_40px] gap-3 border-b bg-muted/50 px-3 py-2 text-xs font-medium">
                  <span>Key *</span>
                  <span>Value *</span>
                  <span>Secret</span>
                  <span className="sr-only">Remove</span>
                </div>
                {fields.map((field, index) => {
                  const row = watched.variables?.[index]
                  const isSecret = row?.isSecret ?? field.isSecret
                  const keepsSavedSecret = isSecret && field.savedSecret
                  const rowLabel = (row?.key ?? '').trim() || `row ${index + 1}`

                  return (
                    <div key={field.id} className="grid grid-cols-[1fr_1.4fr_72px_40px] items-start gap-3 px-3 py-2" data-testid={`variable-row-${index}`}>
                      <div className="flex flex-col gap-1">
                        <Input
                          aria-label={`Key for variable ${index + 1}`}
                          aria-invalid={!!errors.variables?.[index]?.key}
                          {...register(`variables.${index}.key`)}
                        />
                        {errors.variables?.[index]?.key && (
                          <p role="alert" className="text-xs text-destructive">{errors.variables[index]?.key?.message}</p>
                        )}
                      </div>
                      <div className="flex flex-col gap-1">
                        <Input
                          type={isSecret ? 'password' : 'text'}
                          autoComplete={isSecret ? 'new-password' : 'off'}
                          aria-label={`Value for variable ${index + 1}`}
                          aria-invalid={!!errors.variables?.[index]?.value}
                          placeholder={keepsSavedSecret ? KEEP_SECRET_HINT : undefined}
                          {...register(`variables.${index}.value`)}
                        />
                        {errors.variables?.[index]?.value && (
                          <p role="alert" className="text-xs text-destructive">{errors.variables[index]?.value?.message}</p>
                        )}
                      </div>
                      <Controller
                        control={control}
                        name={`variables.${index}.isSecret`}
                        render={({ field: secretField }) => (
                          <Switch
                            className="mt-2"
                            checked={secretField.value}
                            onCheckedChange={secretField.onChange}
                            aria-label={`Secret for variable ${index + 1}`}
                          />
                        )}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-9 w-9"
                        onClick={() => remove(index)}
                        disabled={fields.length === 1}
                        aria-label={`Remove variable ${rowLabel}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )
                })}
              </div>
              <Button type="button" variant="secondary" className="w-fit" onClick={() => append(emptyRow())} disabled={fields.length >= 100}>
                <Plus className="mr-2 h-4 w-4" />
                Add Environment Variable
              </Button>
              <p className="text-xs text-muted-foreground">
                Keys: uppercase letters, digits and _ only, unique across all groups in the project. Secret values are masked
                while typing and can't be viewed after saving{isEdit ? '; leave a saved secret empty to keep it' : ''}.
              </p>
            </fieldset>

            {showRenameWarning && (
              <div role="status" className="flex items-start gap-2 rounded border border-warning-foreground bg-warning px-3 py-2 text-sm text-warning-foreground">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>You changed the group name or a key. Workflows and tests that use the old name may stop working.</span>
              </div>
            )}

            {errors.root?.server && <p role="alert" className="text-sm text-destructive">{errors.root.server.message}</p>}
          </form>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>Cancel</Button>
          <Button type="submit" form="variable-group-form" disabled={isSubmitting || loadingGroup || (isEdit && loadFailed)}>
            {isSubmitting ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Group'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
